import { useState } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PermissionGuard } from '@/components/PermissionGuard';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { MrsLink } from '@/components/MrsLink';
import type { MaterialRequestDto } from '../types';
import { materialRequestService } from '../services/materialRequestService';

export type MrsTableMeta = { onRefresh: () => void };

const columnHelper = createColumnHelper<MaterialRequestDto>();

function SortableHeader({
  label,
  column,
}: {
  label: string;
  column: {
    getIsSorted: () => false | 'asc' | 'desc';
    getToggleSortingHandler: () => ((event: unknown) => void) | undefined;
  };
}) {
  const sorted = column.getIsSorted();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={column.getToggleSortingHandler()}
    >
      {label}
      {sorted === 'asc' ? (
        <ArrowUp className="ml-1 h-3.5 w-3.5" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="ml-1 h-3.5 w-3.5" />
      ) : (
        <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/50" />
      )}
    </Button>
  );
}

function FulfillmentBar({ row }: { row: MaterialRequestDto }) {
  const total = row.items.reduce((sum, i) => sum + i.requestedQuantity, 0);
  const fulfilled = row.items.reduce((sum, i) => sum + i.releasedQuantity, 0);
  const pct = total > 0
    ? (fulfilled >= total ? 100 : Math.floor((fulfilled / total) * 100))
    : 0;
  return (
    <div className="flex items-center gap-2 min-w-[80px]">
      <div className="flex-1 h-2 bg-border rounded-full overflow-hidden">
        <div className="h-full bg-success" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground w-8 text-right">{pct}%</span>
    </div>
  );
}

function ResyncCell({
  id,
  mrsNumber,
  onRefresh,
}: {
  id: number;
  mrsNumber: string;
  onRefresh?: () => void;
}) {
  const [isResyncing, setIsResyncing] = useState(false);

  const handleResync = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResyncing(true);
    try {
      const result = await materialRequestService.resyncMrs(id);
      if (result.errors.length > 0) {
        toast.warning(result.errors[0]);
      } else {
        toast.success(`${mrsNumber} re-synced from NetSuite`);
      }
      onRefresh?.();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to resync MRS');
    } finally {
      setIsResyncing(false);
    }
  };

  return (
    <PermissionGuard permission={PERMISSIONS.ISSUANCE_SYNC_REQUESTS}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResync}
              disabled={isResyncing}
            >
              {isResyncing
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <RefreshCw className="h-4 w-4" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Resync from NetSuite</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </PermissionGuard>
  );
}

export const columns = [
  columnHelper.accessor('mrsNumber', {
    header: ({ column }) => <SortableHeader label="MRS Number" column={column} />,
    cell: (info) => {
      const mrsNumber = info.getValue();
      const forAdjustment = info.row.original.forAdjustment;
      if (!mrsNumber) return <span className="font-mono font-medium">-</span>;
      return (
        <div className="flex items-center gap-2">
          <MrsLink
            mrsId={info.row.original.id}
            mrsNumber={mrsNumber}
            className="font-mono font-medium"
          />
          {forAdjustment && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <AlertTriangle className="h-4 w-4 text-warning" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Flagged for adjustment — NetSuite status no longer eligible</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor('requestorFullName', {
    header: ({ column }) => <SortableHeader label="Requestor" column={column} />,
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor('requestorIdNumber', {
    header: 'Employee ID',
    enableSorting: false,
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor('requestedAt', {
    header: ({ column }) => <SortableHeader label="Requested Date" column={column} />,
    cell: (info) => {
      const value = info.getValue();
      return value
        ? new Date(value).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : '-';
    },
  }),
  columnHelper.accessor('approvedBy', {
    header: 'Approved By',
    enableSorting: false,
    cell: (info) => info.getValue() || '-',
  }),
  columnHelper.accessor((row) => row.items.length, {
    id: 'itemCount',
    header: 'Items',
    enableSorting: false,
    cell: (info) => info.getValue(),
  }),
  columnHelper.display({
    id: 'fulfillment',
    header: 'Fulfillment',
    cell: (info) => <FulfillmentBar row={info.row.original} />,
  }),
  columnHelper.accessor('createdAt', {
    header: 'Synced',
    enableSorting: false,
    cell: (info) =>
      new Date(info.getValue()).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
  }),
  columnHelper.display({
    id: 'actions',
    header: '',
    cell: (info) => (
      <div className="flex items-center gap-1">
        <ResyncCell
          id={info.row.original.id}
          mrsNumber={info.row.original.mrsNumber}
          onRefresh={(info.table.options.meta as MrsTableMeta)?.onRefresh}
        />
      </div>
    ),
  }),
];
